// @flow
import React from 'react';
import { Button } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { sortStrings } from '@fortress-technology-solutions/fortress-component-library/utils';
import { FormattedMessage } from 'react-intl';
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import TableLayoutV2 from '../../../components/TableLayoutV2';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useIntl from '../../../hooks/useIntl';
import PropertyTransactionCodesService from '../../../services/propertyTransactionCodesService';
import {
  createHudVoucherMiscAccountingRecord,
  deleteHudVoucherMiscAccountingRecord,
  getHudVoucherMiscAccountingRecords,
  updateHudVoucherMiscAccountingApprovedAmount,
} from '../../../services/hudVouchersService';
import { useMiscellaneousAccountingTable } from './hooks';
import messages from './messages';
import parentMessages from '../messages';
import ConditionalTooltip from '../../../components/ConditionalTooltip';
import { BASE_TABLE_LAYOUT_SX } from '../constants';
import AddMiscellaneousAccountingModal from './AddMiscellaneousAccountingModal';
import DeleteMiscAccountingModal from './DeleteMiscAccountingModal';
import {
  getMiscAccountingStatusDisplay,
  getMiscRequestTypeLabel,
} from './constants';

interface Props {
  show: boolean;
  organizationId?: string;
  propertyId?: string;
  voucherId?: string;
  voucherSubmissionStatus?: string;
  onVoucherStatusChange?: (newStatus: string) => void;
  onRefreshVoucher?: () => void;
  isReadOnly?: boolean;
  hasVariance?: boolean;
  canManage: boolean;
}

function MiscellaneousAccounting({
  show,
  organizationId,
  propertyId,
  voucherId,
  voucherSubmissionStatus,
  onVoucherStatusChange,
  onRefreshVoucher,
  isReadOnly = false,
  hasVariance = false,
  canManage,
}: Props): React$Node {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const miscAccountingQueryKey = useMemo(
    () => ['hudVoucherMiscAccounting', organizationId, propertyId, voucherId],
    [organizationId, propertyId, voucherId],
  );

  const voucherStatsQueryKey = useMemo(
    () => ['hudVoucherStats', organizationId, propertyId, voucherId],
    [organizationId, propertyId, voucherId],
  );

  const isQueryEnabled = Boolean(
    show && organizationId && propertyId && voucherId,
  );

  const isChargeCodesQueryEnabled = Boolean(
    show && organizationId && propertyId,
  );

  const { data: propertyTransactionCodes = [] } = useQuery(
    ['propertyTransactionCodes', organizationId, propertyId],
    async () => {
      const service = new PropertyTransactionCodesService();
      return service.getPropertyTransactionCodes(organizationId, propertyId);
    },
    {
      enabled: isChargeCodesQueryEnabled,
      staleTime: 5 * 60 * 1000,
      onError: () => {
        toastr.error(
          'Error',
          intl.formatMessage(messages.loadChargeCodesError),
        );
      },
    },
  );

  const transactionCodes = useMemo(() => {
    return (
      (Array.isArray(propertyTransactionCodes)
        ? propertyTransactionCodes
        : propertyTransactionCodes?.codes) ??
      selectedProperty?.transactionCodes?.codes ??
      []
    );
  }, [propertyTransactionCodes, selectedProperty]);

  const chargeCodeOptions = useMemo(() => {
    return transactionCodes
      .filter((code) => {
        const tc = code?.transactionCode;
        return (
          code?.userSelectable === true &&
          tc?.category === 'Misc' &&
          tc?.transactionType?.name === 'Misc Charge'
        );
      })
      .map((code) => {
        const transactionCode = code?.transactionCode?.code ?? '';
        const description = code?.transactionCode?.description ?? '';
        return {
          value: code.id,
          text: `${transactionCode} - ${description}`.trim(),
        };
      })
      .filter((opt) => opt.value && opt.text)
      .sort(sortStrings('text', 'ASC'));
  }, [transactionCodes]);

  const propertyTransactionCodeById = useMemo(() => {
    return (transactionCodes || []).reduce((acc, code) => {
      const id = code?.id;
      const transactionCode = code?.transactionCode?.code;
      if (id && transactionCode) {
        acc[id] = transactionCode;
      }
      return acc;
    }, {});
  }, [transactionCodes]);

  const {
    data: miscellaneousAccounting = [],
    isLoading: isMiscAccountingLoading,
  } = useQuery(
    miscAccountingQueryKey,
    () =>
      getHudVoucherMiscAccountingRecords({
        organizationId: organizationId ?? '',
        propertyId: propertyId ?? '',
        voucherId: voucherId ?? '',
      }),
    {
      enabled: isQueryEnabled,
      onError: () => {
        toastr.error('Error', intl.formatMessage(messages.loadError));
      },
    },
  );

  const miscAccountingForTable = useMemo(() => {
    return (miscellaneousAccounting || []).map((row) => {
      const ptcId = row?.propertyTransactionCodeId;
      return {
        ...row,
        chargeCode: (ptcId && propertyTransactionCodeById[ptcId]) || ptcId,
        requestTypeDisplay: getMiscRequestTypeLabel(row?.requestType),
        statusDisplay: getMiscAccountingStatusDisplay(row?.status),
      };
    });
  }, [miscellaneousAccounting, propertyTransactionCodeById]);

  const { mutateAsync: createMiscAccounting, isLoading: isCreating } =
    useMutation(
      ['createHudVoucherMiscAccounting', organizationId, propertyId, voucherId],
      (payload) =>
        createHudVoucherMiscAccountingRecord({
          organizationId: organizationId ?? '',
          propertyId: propertyId ?? '',
          voucherId: voucherId ?? '',
          payload,
        }),
      {
        onSuccess: async () => {
          const previousStatus = voucherSubmissionStatus;

          toastr.success('Success', intl.formatMessage(messages.createSuccess));

          if (onRefreshVoucher) {
            await onRefreshVoucher();
          }

          if (previousStatus === 'Submitted' && onVoucherStatusChange) {
            onVoucherStatusChange('Draft');
            toastr.warning(
              intl.formatMessage(messages.voucherStatusUpdatedTitle),
              intl.formatMessage(messages.voucherReturnedToDraftWarning),
            );
          }
        },
        onError: () => {
          toastr.error('Error', intl.formatMessage(messages.createError));
        },
      },
    );

  const deleteMiscAccountingMutation = useMutation(
    (miscAccountingId: string) =>
      deleteHudVoucherMiscAccountingRecord({
        organizationId: organizationId ?? '',
        propertyId: propertyId ?? '',
        voucherId: voucherId ?? '',
        miscAccountingId,
      }),
    {
      onSuccess: async () => {
        setDeleteModalOpen(false);
        setSelectedRecord(null);

        const previousStatus = voucherSubmissionStatus;

        toastr.success('Success', intl.formatMessage(messages.deleteSuccess));

        if (onRefreshVoucher) {
          await onRefreshVoucher();
        }

        if (previousStatus === 'Submitted' && onVoucherStatusChange) {
          onVoucherStatusChange('Draft');
          toastr.warning(
            intl.formatMessage(messages.voucherStatusUpdatedTitle),
            intl.formatMessage(messages.voucherReturnedToDraftWarning),
          );
        }
      },
      onError: (error: any) => {
        let errorMessage = intl.formatMessage(messages.deleteError);

        if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error?.error) {
          errorMessage = error.error;
        }

        if (errorMessage.includes('paid')) {
          errorMessage = intl.formatMessage(messages.deletePaidError);
        } else if (errorMessage.includes('not found')) {
          errorMessage = intl.formatMessage(messages.deleteNotFoundError);
        } else if (errorMessage.includes('Feature flag')) {
          errorMessage = intl.formatMessage(
            messages.deleteFeatureUnavailableError,
          );
        }

        toastr.error('Error', errorMessage);
      },
    },
  );
  const handleAddNew = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleDeleteClick = useCallback((record) => {
    setSelectedRecord(record);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const miscAccountingId = selectedRecord?.id;
    if (miscAccountingId) {
      deleteMiscAccountingMutation.mutate(miscAccountingId);
    } else {
      toastr.error('Error', intl.formatMessage(messages.deleteMissingIdError));
    }
  }, [selectedRecord, deleteMiscAccountingMutation, intl]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedRecord(null);
  }, []);
  const handleSubmitNewMiscAccounting = useCallback(
    async (payload) => {
      await createMiscAccounting(payload);
      setIsAddModalOpen(false);
    },
    [createMiscAccounting],
  );

  const { mutateAsync: updateApprovedAmount } = useMutation(
    ({ miscAccountingId, approvedAmount }) =>
      updateHudVoucherMiscAccountingApprovedAmount({
        organizationId: organizationId ?? '',
        propertyId: propertyId ?? '',
        voucherId: voucherId ?? '',
        miscAccountingId,
        approvedAmount,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(miscAccountingQueryKey);
        await queryClient.invalidateQueries(voucherStatsQueryKey);
        toastr.success(
          'Success',
          intl.formatMessage(messages.updateApprovedAmountSuccess),
        );
      },
      onError: () => {
        toastr.error(
          'Error',
          intl.formatMessage(messages.updateApprovedAmountError),
        );
      },
    },
  );

  const handleSaveApprovedAmount = useCallback(
    (miscAccountingRow, approvedAmount) =>
      updateApprovedAmount({
        miscAccountingId: miscAccountingRow.id,
        approvedAmount,
      }),
    [updateApprovedAmount],
  );

  const {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    count,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    isLoading,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount,
  } = useMiscellaneousAccountingTable({
    isReadOnly,
    miscellaneousAccounting: miscAccountingForTable,
    onDelete: isReadOnly ? undefined : handleDeleteClick,
    voucherSubmissionStatus,
    hasVariance,
    onSaveApprovedAmount: handleSaveApprovedAmount,
    canManage,
  });

  if (!show) {
    return null;
  }

  return (
    <>
      <TableLayoutV2 name={name} fullPage sx={BASE_TABLE_LAYOUT_SX}>
        {(height) => (
          <>
            <TableHeader
              title={<FormattedMessage {...messages.tableTitle} />}
              count={count}
              totalCount={totalCount}
              actions={
                <ConditionalTooltip
                  show={!canManage}
                  title={intl.formatMessage(parentMessages.noPermissionTooltip)}
                  placement="top"
                >
                  <Button
                    startIcon={<AddIcon />}
                    variant="shout"
                    onClick={handleAddNew}
                    disabled={!isQueryEnabled || isReadOnly || !canManage}
                  >
                    {intl.formatMessage(messages.addNew)}
                  </Button>
                </ConditionalTooltip>
              }
            />

            <Table
              {...baseTableProps}
              name={name}
              allColumnsHidden={allColumnsHidden}
              columnOptions={columnOptions}
              count={count}
              dateState={dateState}
              filterState={filterState}
              filterTypeState={filterTypeState}
              handleColumnChange={handleColumnChange}
              handleDateSubmit={handleDateSubmit}
              handleFilterChange={handleFilterChange}
              handleFilterTypeChange={handleFilterTypeChange}
              handleSearchSubmit={handleSearchSubmit}
              handleSortChange={handleSortChange}
              headers={filteredHeaders}
              height={height}
              isLoading={isLoading || isMiscAccountingLoading}
              order={order}
              orderBy={orderBy}
              rows={rows}
              searchState={searchState}
              selectedColumns={selectedColumns}
              totalCount={totalCount}
              emptyStateMessage={baseTableProps.intl.formatMessage(
                messages.emptyStateMessage,
              )}
            />
          </>
        )}
      </TableLayoutV2>

      <AddMiscellaneousAccountingModal
        open={isAddModalOpen}
        intl={intl}
        chargeCodeOptions={chargeCodeOptions}
        isSubmitting={isCreating}
        onClose={handleCloseAddModal}
        onSubmit={handleSubmitNewMiscAccounting}
      />

      <DeleteMiscAccountingModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        intl={intl}
        isDeleting={deleteMiscAccountingMutation.isLoading}
      />
    </>
  );
}

export default MiscellaneousAccounting;
